// redux/slices/feedBackSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { notify, notifyAPIMessages } from "../../../helpers/commonFunctions";
import { authHeaders } from "../../../helpers/axios";
import { apiEndPoints } from "../../../helpers/constants";
import { BASE_URL } from "../../../config";

// Get Feedback Types
export const reqToGetFeedBackType = createAsyncThunk(
  "reqToGetFeedBackType",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.feedbackType}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Get Feedback Status
export const reqToGetFeedBackStatus = createAsyncThunk(
  "reqToGetFeedBackStatus",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.feedbackStatus}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Create Feedback
export const reqToCreateFeedBack = createAsyncThunk(
  "reqToCreateFeedBack",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.createFeedback}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Get All Feedback
export const reqToGetAllFeedback = createAsyncThunk(
  "reqToGetAllFeedback",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const { page = 1, limit = 10, ...filters } = data;

      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.getAllFeedback}?page=${page}&limit=${limit}`,
        {
          ...authHeaders(),
          params: filters,
        }
      );

      onSuccess(response);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Get Feedback By ID
export const reqToGetFeedbackById = createAsyncThunk(
  "reqToGetFeedbackById",
  async ({ id, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.getFeedbackById}/${id}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Update Feedback (Tenant - Edit details)
export const reqToUpdateFeedback = createAsyncThunk(
  "reqToUpdateFeedback",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.updateFeedback}/${id}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Admin Update Feedback Actions (Change Status, Assign Staff, Change Priority)
export const reqToUpdateFeedbackAdmin = createAsyncThunk(
  "reqToUpdateFeedbackAdmin",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.adminUpdateFeedback}/${id}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Assign Feedback
export const reqToAssignFeedback = createAsyncThunk(
  "reqToAssignFeedback",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const payload = {
        action: "assign_staff",
        value: data.assigneeId,
      };

      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.adminUpdateFeedback}/${id}`,
        payload,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Update Feedback Status
export const reqToUpdateFeedbackStatus = createAsyncThunk(
  "reqToUpdateFeedbackStatus",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const payload = {
        action: "change_status",
        value: data,
      };

      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.adminUpdateFeedback}/${id}`,
        payload,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToUpdateFeedbackData = createAsyncThunk(
  "reqToUpdateFeedbackData",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.adminUpdateFeedback}/${id}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Update Feedback Priority
export const reqToUpdateFeedbackPriority = createAsyncThunk(
  "reqToUpdateFeedbackPriority",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const payload = {
        action: "change_priority",
        value: data.priority,
      };

      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.adminUpdateFeedback}/${id}`,
        payload,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Delete Feedback
export const reqToDeleteFeedback = createAsyncThunk(
  "reqToDeleteFeedback",
  async ({ id, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}${apiEndPoints.deleteFeedback}/${id}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Add Vote
export const reqToAddVote = createAsyncThunk(
  "reqToAddVote",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.addVote}/${id}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Add Comment
export const reqToAddComment = createAsyncThunk(
  "reqToAddComment",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${BASE_URL}${apiEndPoints.addComment}/${id}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Update Comment
export const reqToUpdateComment = createAsyncThunk(
  "reqToUpdateComment",
  async ({ id, data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${BASE_URL}${apiEndPoints.updateComment}/${id}`,
        data,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

// Delete Comment
export const reqToDeleteComment = createAsyncThunk(
  "reqToDeleteComment",
  async ({ id, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${BASE_URL}${apiEndPoints.deleteComment}/${id}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetroadmap = createAsyncThunk(
  "reqToGetroadmap",
  async ({ data = {}, onSuccess, onFailure }, thunkAPI) => {
    try {
      const { type, status, sort, ...otherFilters } = data;

      const response = await axios.get(`${BASE_URL}${apiEndPoints.roadmap}`, {
        ...authHeaders(),
        params: {
          ...(type && type !== "all" && { type }),
          ...(status && status !== "all" && { status }),
          ...(sort && { sort }),
          ...otherFilters,
        },
      });

      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetdashboarddata = createAsyncThunk(
  "reqToGetdashboarddata",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${BASE_URL}${apiEndPoints.dashboard}`,
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

export const reqToGetFeedBackAnalytics = createAsyncThunk(
  "reqToGetFeedBackAnalytics",
  async ({ data, onSuccess, onFailure }, thunkAPI) => {
    try {
      // Build query parameters dynamically
      const queryParams = new URLSearchParams();

      // Add all properties from data as query parameters
      Object.keys(data).forEach((key) => {
        if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
          queryParams.append(key, data[key]);
        }
      });

      const response = await axios.post(
        `${BASE_URL}${
          apiEndPoints.feedbackAnalytics
        }?${queryParams.toString()}`,
        {},
        authHeaders()
      );
      onSuccess(response?.data);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Network Error",
        status: error?.response?.status,
      });
    }
  }
);

const FeedBackSlice = createSlice({
  name: "feedback",
  initialState: {
    loader: false,
    error: null,
    type: [],
    priority: [],
    status: [],
    feedbackList: [],
    feedbackDetail: null,
    createProgramLoader: false,
    createProgramError: null,
    updateLoader: false,
    updateError: null,
    roadmapList: [],
    dashboardData: [],
    feedbackAnalytics: [],
  },
  reducers: {
    clearFeedbackDetail: (state) => {
      state.feedbackDetail = null;
    },
    clearFeedbackError: (state) => {
      state.error = null;
      state.createProgramError = null;
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    // Get Feedback Types
    builder.addCase(reqToGetFeedBackType.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetFeedBackType.fulfilled, (state, action) => {
      state.loader = false;
      state.type = action.payload?.data?.data || [];
    });
    builder.addCase(reqToGetFeedBackType.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // Get Feedback Status
    builder.addCase(reqToGetFeedBackStatus.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetFeedBackStatus.fulfilled, (state, action) => {
      state.loader = false;
      state.status = action.payload?.data?.data || [];
    });
    builder.addCase(reqToGetFeedBackStatus.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // Create Feedback
    builder.addCase(reqToCreateFeedBack.pending, (state) => {
      state.createProgramLoader = true;
      state.createProgramError = null;
    });
    builder.addCase(reqToCreateFeedBack.fulfilled, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = null;
      notify("Feedback created successfully", "success");
    });
    builder.addCase(reqToCreateFeedBack.rejected, (state, action) => {
      state.createProgramLoader = false;
      state.createProgramError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to create feedback");
    });

    // Get All Feedback
    builder.addCase(reqToGetAllFeedback.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetAllFeedback.fulfilled, (state, action) => {
      state.loader = false;
      state.feedbackList = action.payload?.data?.data || [];
    });
    builder.addCase(reqToGetAllFeedback.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to fetch feedback");
    });

    // Get Feedback By ID
    builder.addCase(reqToGetFeedbackById.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetFeedbackById.fulfilled, (state, action) => {
      state.loader = false;
      state.feedbackDetail = action.payload?.data?.data || null;
    });
    builder.addCase(reqToGetFeedbackById.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(
        action.payload?.message || "Failed to fetch feedback details"
      );
    });

    // Update Feedback (Tenant)
    builder.addCase(reqToUpdateFeedback.pending, (state) => {
      state.updateLoader = true;
      state.updateError = null;
    });
    builder.addCase(reqToUpdateFeedback.fulfilled, (state, action) => {
      state.updateLoader = false;
      state.updateError = null;
      notify("Feedback updated successfully", "success");
    });
    builder.addCase(reqToUpdateFeedback.rejected, (state, action) => {
      state.updateLoader = false;
      state.updateError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to update feedback");
    });

    // Assign Feedback
    builder.addCase(reqToAssignFeedback.pending, (state) => {
      state.updateLoader = true;
      state.updateError = null;
    });
    builder.addCase(reqToAssignFeedback.fulfilled, (state, action) => {
      state.updateLoader = false;
      state.updateError = null;
      notify("Feedback assigned successfully", "success");
    });
    builder.addCase(reqToAssignFeedback.rejected, (state, action) => {
      state.updateLoader = false;
      state.updateError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to assign feedback");
    });

    // Update Feedback Status
    builder.addCase(reqToUpdateFeedbackStatus.pending, (state) => {
      state.updateLoader = true;
      state.updateError = null;
    });
    builder.addCase(reqToUpdateFeedbackStatus.fulfilled, (state, action) => {
      state.updateLoader = false;
      state.updateError = null;
    });
    builder.addCase(reqToUpdateFeedbackStatus.rejected, (state, action) => {
      state.updateLoader = false;
      state.updateError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to update status");
    });

    // Update Feedback Priority
    builder.addCase(reqToUpdateFeedbackPriority.pending, (state) => {
      state.updateLoader = true;
      state.updateError = null;
    });
    builder.addCase(reqToUpdateFeedbackPriority.fulfilled, (state, action) => {
      state.updateLoader = false;
      state.updateError = null;
      notify("Priority updated successfully", "success");
    });
    builder.addCase(reqToUpdateFeedbackPriority.rejected, (state, action) => {
      state.updateLoader = false;
      state.updateError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to update priority");
    });

    // Delete Feedback
    builder.addCase(reqToDeleteFeedback.pending, (state) => {
      state.updateLoader = true;
      state.updateError = null;
    });
    builder.addCase(reqToDeleteFeedback.fulfilled, (state, action) => {
      state.updateLoader = false;
      state.updateError = null;
      notify("Feedback deleted successfully", "success");
    });
    builder.addCase(reqToDeleteFeedback.rejected, (state, action) => {
      state.updateLoader = false;
      state.updateError = action.payload;
      notifyAPIMessages(action.payload?.message || "Failed to delete feedback");
    });

    // Add Vote
    builder.addCase(reqToAddVote.pending, (state) => {
      state.updateLoader = true;
    });
    builder.addCase(reqToAddVote.fulfilled, (state, action) => {
      state.updateLoader = false;
      notify("Vote submitted successfully", "success");
    });
    builder.addCase(reqToAddVote.rejected, (state, action) => {
      state.updateLoader = false;
      notifyAPIMessages(action.payload?.message || "Failed to submit vote");
    });

    // Get Feedback Status
    builder.addCase(reqToGetroadmap.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetroadmap.fulfilled, (state, action) => {
      state.loader = false;
      state.roadmapList = action.payload?.data?.data || [];
    });
    builder.addCase(reqToGetroadmap.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // Get Feedback Status
    builder.addCase(reqToGetdashboarddata.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetdashboarddata.fulfilled, (state, action) => {
      state.loader = false;
      state.dashboardData = action.payload?.data?.data || [];
    });
    builder.addCase(reqToGetdashboarddata.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });

    // Get Feedback Status
    builder.addCase(reqToGetFeedBackAnalytics.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToGetFeedBackAnalytics.fulfilled, (state, action) => {
      state.loader = false;
      state.feedbackAnalytics = action.payload?.data?.data || [];
    });
    builder.addCase(reqToGetFeedBackAnalytics.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload?.message || "Something went wrong");
    });
  },
});

export const { clearFeedbackDetail, clearFeedbackError } =
  FeedBackSlice.actions;
export default FeedBackSlice.reducer;
